import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center gap-10 whitespace-nowrap',
    'rounded-none',
    'focus-visible:outline-none focus-visible:shadow-focus',
    'disabled:cursor-not-allowed disabled:opacity-40',
  ].join(' '),
  {
    variants: {
      variant: {
        // Mono caps variants (small CTAs, navigation, inline actions)
        primary:
          'justify-center text-15 leading-100 bg-ink !text-white hover:bg-[#252525] active:bg-black',
        secondary:
          'justify-center text-15 leading-100 bg-raised text-ink border border-slate',
        ghost:
          'justify-center text-15 leading-100 bg-transparent text-ink hover:bg-sunken',
        danger:
          'justify-center text-15 leading-100 bg-danger text-white hover:brightness-95',
        icon:
          'justify-center px-0 w-40 gap-0 bg-transparent text-ink hover:bg-sunken',

        // Filled CTA — mirrors system-web's `.btn .is-xl` intake "Continue"
        // button (`/Users/jakepfahl/Web/system/system-web/styles/_modules/_buttons.css`):
        // black bg + white text + py-20 + justify-center + sentence case
        // ModernGothic. Trailing Icon name="Arrow" expected as a child.
        // This is the primary CTA pattern across the portal.
        filled:
          'justify-between font-body text-15 leading-100 bg-black !text-white hover:bg-[#252525] active:bg-black py-20 px-15 w-full',
      },
      size: {
        sm: 'h-32 px-15 text-10',
        md: 'h-40 px-15 text-14',
        lg: 'h-48 px-25 text-15',
      },
    },
    compoundVariants: [
      // `filled` ignores the height-based size scale (it sizes by py-20 padding)
      // but accepts a size hint for text scale.
      { variant: 'filled', size: 'sm', class: 'h-auto text-15' },
      { variant: 'filled', size: 'md', class: 'h-auto text-15' },
      { variant: 'filled', size: 'lg', class: 'h-auto text-16' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Spinner /> : children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

function Spinner() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      className="animate-spin"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
  );
}
